/**
 * Portfolio data for the Portfolio module
 */
export const portfolioData = [
  {
    id: "indigorgeous",
    title: "Indigorgeous Technical Manual",
    type: "Technical Documentation",
    thumbnail: "indigorgeous-thumbnail.jpg", // Placeholder for image
    description: "Comprehensive 74-page technical reference manual covering printing technology, special effects, and production processes. Currently being converted from its original PDF format into an interactive web-based experience, making the content more accessible and engaging while maintaining its technical depth.",
    skills: ["Technical Writing", "Visual Communication", "Information Architecture", "Web Development"],
    highlights: [
      "Converted complex technical processes into accessible educational content",
      "Created detailed diagrams and process visualizations",
      "Established standardized terminology and procedures",
      "Improved client understanding of technical capabilities",
      "Currently developing interactive web version for enhanced accessibility"
    ],
    urls: {
      "Interactive Version": "http://indigorgeous.madlab.science/",
      "Download PDF": "https://www.madlab.science/s/Indigorgeous-Print-Finishing-Spot-Colors-and-Special-Effects.pdf"
    }
  },
  {
    id: "indigo-edge",
    title: "IndigoEdge Webinar Series",
    type: "Educational Content",
    thumbnail: "indigo-edge-thumbnail.jpg", // Placeholder for image
    description: "Quarterly technical education webinar series focused on printing techniques, special effects, and production considerations. Each webinar includes interactive demonstrations and visual learning materials.",
    skills: ["Instructional Design", "Presentation Development", "Visual Learning"],
    highlights: [
      "Developed comprehensive presentation scripts with technical explanations",
      "Created interactive demonstrations of print processes",
      "Designed visual learning aids to explain complex concepts",
      "Established progressive learning path across multiple webinars"
    ],
    urls: {
      "Watch Webinars": "https://www.youtube.com/playlist?list=PLkaBIj0d3IqWkEeAMfnfQA4RMNv-MYTE6"
    }
  },
  {
    id: "press-perspective",
    title: "PressPective Viewer",
    type: "Interactive Learning Tool",
    thumbnail: "presspective-thumbnail.jpg", // Placeholder for image
    description: "Complex and fully functional OpenSeadragon viewer that supports up to 4 synchronized viewer windows, allowing detailed comparison of output from different printing presses. Educational tool for understanding print quality differences.",
    skills: ["Web Development", "Interactive Design", "Educational Technology"],
    highlights: [
      "Engineered synchronized multi-window comparison tool",
      "Implemented annotation capabilities for guided learning",
      "Created collapsible metadata sidebar with technical specifications",
      "Developed filtering system for selecting different print samples"
    ],
    urls: {
      Explore: "https://www.madlab.science/presspective-viewer",
    }
  },
  {
    id: "print-axis",
    title: "PrintAxis Paper Size Explorer",
    type: "Interactive Learning Tool",
    thumbnail: "printaxis-thumbnail.jpg", // Placeholder for image
    description: "Full screen web application for exploring paper sizes and standards. Includes comprehensive library of standard sheet sizes with dynamic scaling for visual comparison.",
    skills: ["Web Development", "Information Design", "Educational Technology"],
    highlights: [
      "Created intuitive visualization of paper size relationships",
      "Implemented conversion tools for different measurement systems",
      "Developed dynamic comparison feature for size evaluation",
      "Built educational modules explaining paper size standards"
    ],
    urls: {
      Explore: "https://www.madlab.science/printaxis",
    }
  },
  {
    id: "duplo-manual",
    title: "Duplo DC-746 Technical Manual",
    type: "Interactive Documentation",
    thumbnail: "duplo-manual-thumbnail.jpg", // Placeholder for image
    description: "Comprehensive interactive operator manual for complex finishing equipment, featuring visual diagrams, troubleshooting guides, and step-by-step setup instructions with interactive elements. Available in both interactive web format and downloadable PDF.",
    skills: ["Technical Writing", "Interactive Documentation", "Visual Instruction", "React Development"],
    highlights: [
      "Converted traditional technical manual into an interactive web-based experience",
      "Created interactive diagrams with clickable machine components",
      "Developed searchable troubleshooting system with severity indicators",
      "Built interactive job setup wizard with step-by-step visualization",
      "Maintained original PDF version for offline reference"
    ],
    urls: {
      "Interactive Version": "internal:duplo-documentation",
      "Download PDF": "https://www.madlab.science/s/DC-746-Guide.pdf"
    },
    hasInteractiveDemo: true
  },
  {
    id: "foil-sop",
    title: "Digital Foil Production Guide",
    type: "Process Documentation",
    thumbnail: "foil-sop-thumbnail.jpg", // Placeholder for image
    description: "Comprehensive technical documentation and educational content covering digital sleeking foil production. Includes detailed setup procedures, production workflows, and troubleshooting guides. The documentation has been used to produce award-winning multi-layer foil art prints and has been featured in industry workshops and media coverage.",
    skills: ["Technical Writing", "Process Documentation", "Quality Control", "Workshop Presentation", "Media Relations"],
    highlights: [
      "Created comprehensive technical documentation for digital foil production",
      "Documentation used to produce award-winning 10-layer foil art print",
      "Featured in PaperSpecs Live Chicago 2024 workshop on digital foil techniques",
      "Received media coverage for innovative foil printing applications",
      "Established standardized procedures for consistent production results"
    ],
    urls: {
      "Technical SOP": "https://www.madlab.science/s/Foil-SOP.pdf",
      "The Kiss Media Coverage": "https://www.youtube.com/watch?v=Jve0VmutDX8",
      "PaperSpecs Live Workshop": "https://www.youtube.com/watch?v=SwTb2b-8shA"
    }
  },
  {
    id: "render-intent",
    title: "Color Rendering Simulation",
    type: "Interactive Educational Tool",
    thumbnail: "render-intent-thumbnail.jpg", // Placeholder for image
    description: "Animated simulation of rendering intents using scientifically accurate transformations applied to an SVG of the CIELAB color space to illustrate how colors are converted between RGB and CMYK standards.",
    skills: ["Web Development", "Color Theory", "Educational Technology"],
    highlights: [
      "Created accurate visualization of complex color science concepts",
      "Implemented interactive controls for different rendering intents",
      "Developed animated transitions showing transformation effects",
      "Provided educational context for technical color management concepts"
    ],
    urls: {
      "Explore Simulation": "https://www.madlab.science/renderingwithintent"
    },
    hasInteractiveDemo: true
  },
  {
    id: "3d-space-planning",
    title: "3D Modeling and Space Planning",
    type: "Visualization & Optimization",
    thumbnail: "3d-space-planning-thumbnail.jpg",
    description: "Comprehensive 3D modeling and spatial planning solutions for retail and production environments. Created detailed, dimensionally accurate models using Google SketchUp to prototype layouts, optimize workflows, and visualize customer experiences. Projects include retail store planning and production facility optimization.",
    skills: ["3D Modeling", "Space Planning", "Workflow Optimization", "Visual Merchandising", "Process Engineering"],
    highlights: [
      "Created detailed, dimensionally accurate 3D model of Jacksonville store using Google SketchUp",
      "Engineered precise digital replicas of modular fixtures for virtual arrangement",
      "Developed comprehensive spatial planning tool for customer pathways and ADA compliance",
      "Eliminated physical merchandising tests through virtual prototyping",
      "Implemented virtual visualization system for management walkthroughs",
      "Optimized merchandise placement through virtual customer journey analysis",
      "Developed comprehensive 3D model of Columbia production facility",
      "Reduced material handling time by 15% through optimized inventory staging",
      "Reduced production bottlenecks through data-driven equipment repositioning"
    ],
    urls: {
      "View Gallery": "https://www.madlab.science/3d-space-planning"
    }
  },
  {
    id: "calculator-suite",
    title: "Print Production Calculator Suite",
    type: "Technical Tools",
    thumbnail: "calculator-suite-thumbnail.jpg", // Placeholder for image
    description: "Comprehensive suite of specialized calculation tools for print production and business operations. Includes spine width calculator, imposition optimizer, and slitter/cutter configuration tools. Built with ActiveX controls, VBA automation, and ODBC database integration for seamless workflow integration and data persistence.",
    skills: ["Software Development", "Process Optimization", "Technical Analysis", "VBA Development", "Database Integration"],
    highlights: [
      "Developed complex calculation algorithms for production tasks",
      "Created user-friendly interfaces for technical tools",
      "Implemented error checking and validation for reliable results",
      "Engineered database integration for automatic parameter loading",
      "Built ActiveX controls for enhanced user interaction",
      "Integrated ODBC connections for enterprise data access"
    ],
    urls: {
      "Explore Tools": "https://www.madlab.science/calculators"
    }
  }
];
